import React, { Component } from 'react';
import PropTypes from 'prop-types';
import parseInt from 'lodash/parseInt';
import classNames from 'classnames/bind';
import styles from './Grid.css';

const cx = classNames.bind(styles);

export class Grid extends Component {
    static numberOfCol = 4; // default column value is 4. Possible values 2,4,6

    static numberOfColumns = Grid.numberOfCol;

    static propTypes = {
        irisComponents: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        adobeAudience: PropTypes.string,
        numberOfColumns: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        items: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        gridStyle: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]),
        hasBorder: PropTypes.bool,
        pageType: PropTypes.string,
        preferences: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        deviceType: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        featureFlags: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        listParentRef: PropTypes.func,
        scrollable: PropTypes.bool,
        onImageLoad: PropTypes.func,
        /**
         * Unique name for referencing dom element in automation testing
         * @type {String}
         */
    };

    static defaultProps = {
        irisComponents: {},
        adobeAudience: '',
        numberOfColumns: Grid.numberOfCol,
        items: {},
        gridStyle: 'none', // Possible values are comfy, cozy, compact, none
        hasBorder: true,
        pageType: '',
        preferences: {},
        deviceType: {},
        automationId: '',
        featureFlags: '',
        subType: '',
        headline: '',
        description: '',
        seeDetailsLink: {},
        disclaimer: '',
        primaryLink: {},
        largeMedia: {},
        mediumMedia: {},
        smallMedia: {},
        scrollable: false,
        listParentRef: () => {},
        onImageLoad: () => {},
    };

    constructor(props) {
        super(props);
        this.renderColumns = this.renderColumns.bind(this);
    }

    getComponentStyle(componentType) {
        let borderStyle = {};
        /* istanbul ignore next */
        const isComponentWithBorder =
            componentType !== 'nucleus' && componentType !== 'imagebanner';

        borderStyle = {
            border: this.props.hasBorder && isComponentWithBorder ? '1px solid #dddddd' : 'none',
        };

        return borderStyle;
    }

    checkComponentToExist(item, index) {
        let ComponentName = null;
        let componentDom = null;
        try {
            /* istanbul ignore else */
            if (item && item.componentType) {
                item.content.componentType = item.componentType;
                item.content.numberOfColumns = item.column || '';
                ComponentName = this.props.irisComponents[item.componentType];
                /* istanbul ignore next */
                if (ComponentName) {
                    componentDom = this.renderComponents(ComponentName, item, index);
                } else {
                    console.warn(
                        'IRIS: Component name is not there in the export list',
                        item.componentType
                    );
                }
            }
        } catch (e) {
            /* istanbul ignore next */
            console.warn('IRIS: component name missmatch, check Iris Response');
        }
        return componentDom;
    }

    // TODOPR: Below code is duplicated between Grid and IRISPage.jsx. Refactor this to use hooks to share
    // the rendering logic
    renderComponents(CustomComponent, componentProps, index) {
        if (!CustomComponent || !componentProps) {
            return null; // Unknown rendering attributes
        }
        const componentContent = (componentProps && componentProps.content) || {};
        const ComponentToRender = CustomComponent.component;
        const rndKeyString = `comp_${componentProps.componentType}_${index}`;
        const {
            adobeAudience,
            deviceType,
            featureFlags,
            pageType,
            preferences,
            onImageLoad,
        } = this.props;
        const { mapToProps } = CustomComponent;
        const automationId = componentProps.componentType;

        let mappedProps = {};
        const baseProps = {
            pageType,
            deviceType,
            preferences,
            automationId,
            adobeAudience,
            featureFlags,
            irisComponentsWithoutGrid: this.irisComponentsWithoutGrid,
            componentType: componentProps.componentType,
            onImageLoad,
        };
        /* istanbul ignore next */
        if (mapToProps) {
            mappedProps = mapToProps(
                componentContent,
                baseProps.pageType,
                baseProps.deviceType,
                baseProps.preferences,
                baseProps.automationId,
                baseProps.featureFlags,
                baseProps.irisComponentsWithoutGrid,
                baseProps.componentType
            );
        } else {
            mappedProps = {
                ...componentProps.content,
                ...baseProps,
            };
        }
        // TODO: action handling is pending
        return <ComponentToRender key={rndKeyString} {...mappedProps} />;
    }

    renderColumns() {
        const {
            numberOfColumns,
            gridStyle,
            items,
            scrollable,
            featureFlags: { enableGridOverlay = true },
        } = this.props;
        const colsClassName = {
            2: cx('col2', gridStyle),
            3: cx('col3', gridStyle),
            4: cx('col4', gridStyle),
            6: cx('col6', gridStyle),
        };
        let componentData = null;
        let componentType = '';

        if ([2, 3, 4, 6].indexOf(parseInt(numberOfColumns)) >= 0)
            Grid.numberOfColumns = numberOfColumns;
        try {
            if (items.length) {
                const filteredItem = items.filter((j) => j !== null);
                if (enableGridOverlay) {
                    componentData = filteredItem.map((item, i) => {
                        let overlayText = '';
                        let itemClass = '';
                        componentType = item.componentType;
                        if (item.content && item.content.overlay) {
                            const { gridOverlay = [] } = this.props.preferences;
                            overlayText = gridOverlay[item.content.overlay];
                            itemClass = overlayText !== undefined ? 'normal-items' : '';
                        }
                        return (
                            <div
                                key={`grid${i + 1}`}
                                className={cx(
                                    scrollable ? 'sliderItem' : colsClassName[Grid.numberOfColumns]
                                )}
                            >
                                <div
                                    className={cx('overlay-main')}
                                    style={this.getComponentStyle(componentType)}
                                >
                                    {overlayText ? (
                                        <div className={cx('overlay-text', itemClass)}>
                                            {overlayText}
                                        </div>
                                    ) : (
                                        ''
                                    )}
                                    {this.checkComponentToExist(item, i)}
                                </div>
                            </div>
                        );
                    });
                } else {
                    componentData = filteredItem.map((item, i) => (
                        <div
                            key={`grid${i + 1}`}
                            className={cx(
                                scrollable ? 'sliderItem' : colsClassName[Grid.numberOfColumns]
                            )}
                        >
                            <div style={this.getComponentStyle(item.componentType)}>
                                {this.checkComponentToExist(item, i)}
                            </div>
                        </div>
                    ));
                }
            }
        } catch (e) {
            /* istanbul ignore next */
            console.warn('no components to render in grid component');
        }
        return componentData;
    }

    render() {
        const { listParentRef, scrollable } = this.props;
        return (
            <div className={cx(scrollable ? 'sliderGrid' : 'colBlock')} ref={listParentRef}>
                {this.renderColumns()}
            </div>
        );
    }
}

export default Grid;
